import classNames from 'classnames';
import React, { CSSProperties, ReactNode, useEffect, useRef } from 'react';

export interface MenuProps {
  /**
   * Aria label for menu accessibility
   */
  ariaLabel?: string;
  /**
   * Dropdown option items. For intended design, use MenuItem.
   */
  children?: ReactNode | null;
  /**
   * Whether the Menu has a box shadow.
   */
  disableShadow?: boolean;
  /**
   * Whether the MenuItems have places for icons.
   */
  hasIcons?: boolean;
  /**
   * A unique identifier for the Menu.
   */
  id?: string;
  /**
   * Whether the Menu is a submenu of another MenuItem.
   */
  isSubMenu?: boolean;
  /**
   * Maximum height of the Menu container. Use CSS syntax (e.g. '300px'). Submenus do not support maxHeight.
   */
  maxHeight?: string;
  /**
   * Function to close the Menu (dropdown). To be handled from outside.
   */
  onClose?: () => void;
  /**
   * Whether the Menu (dropdown) is open. To be handled from outside.
   */
  open?: boolean;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
}

export function Menu({
  ariaLabel,
  children,
  disableShadow = false,
  hasIcons = false,
  id,
  isSubMenu = false,
  maxHeight,
  onClose,
  open,
  style,
}: MenuProps) {
  const menuRef = useRef<HTMLUListElement>(null);

  // Give focus on the first child of the Menu that has the role "menuitem" when the Menu is opened.
  useEffect(() => {
    if (open) {
      const firstMenuItem =
        menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
      if (firstMenuItem) {
        firstMenuItem.focus();
      }
    }
  }, [open]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();

      if (isSubMenu) {
        const previousSibling = menuRef.current?.previousElementSibling;
        if (previousSibling) {
          (previousSibling as HTMLElement).focus();
        }
      }

      (event.target as HTMLElement).blur();

      event.stopPropagation();

      onClose?.();
    }
  };

  return isSubMenu ? (
    <ul
      aria-hidden={!open}
      aria-label={ariaLabel}
      className={classNames(
        'fd-menu__sublist',
        disableShadow && 'fd-menu__sublist--no-shadow',
      )}
      id={id}
      onKeyDown={onKeyDown}
      ref={menuRef}
      role="menu"
      style={style}
      tabIndex={-1}
    >
      {children}
    </ul>
  ) : (
    <nav
      className={classNames(
        'fd-menu',
        maxHeight && !isSubMenu && 'fd-menu--overflow',
        hasIcons && 'fd-menu--icons',
      )}
      style={{ ...style, maxHeight: `${maxHeight}` }}
    >
      <ul
        aria-hidden={!open}
        aria-label={ariaLabel}
        className={classNames(
          'fd-menu__list',
          disableShadow && 'fd-menu__list--no-shadow',
        )}
        id={id}
        onKeyDown={onKeyDown}
        ref={menuRef}
        role="menu"
        tabIndex={-1}
      >
        {children}
      </ul>
    </nav>
  );
}
